<template>
    <div class="admin-chat-manager">
        <el-card shadow="never" class="manager-card">
            <template #header>
                <div class="card-header">
                    <span class="title">聊天监测管理</span>
                    <div class="header-actions">
                        <el-input v-model="searchQuery" placeholder="搜索聊天内容 (敏感词监测)" clearable style="width: 250px"
                            @keyup.enter="handleSearch" @clear="handleSearch">
                            <template #prefix>
                                <el-icon>
                                    <Search />
                                </el-icon>
                            </template>
                        </el-input>

                        <el-input v-model="searchUserId" placeholder="按用户ID搜索" clearable style="width: 150px"
                            @keyup.enter="handleSearch" @clear="handleSearch">
                            <template #prefix>
                                <el-icon>
                                    <User />
                                </el-icon>
                            </template>
                        </el-input>

                        <el-button type="primary" @click="handleSearch">搜索</el-button>
                    </div>
                </div>
            </template>

            <el-table :data="messages" v-loading="loading" border stripe style="width: 100%">
                <el-table-column label="聊天室" prop="roomId" width="80" align="center" />

                <el-table-column label="发送者" width="180">
                    <template #default="{ row }">
                        <div class="user-cell clickable-user" @click="openHistoryDrawer(row)" title="点击查看聊天上下文">
                            <el-avatar :size="30" :src="row.avatar || defaultAvatar" />
                            <div class="user-info">
                                <span class="username">{{ row.senderName }}</span>
                                <span class="user-id">ID: {{ row.senderId }}</span>
                            </div>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="消息类型" width="100" align="center">
                    <template #default="{ row }">
                        <el-tag
                            :type="row.msgType === 'text' ? 'info' : (row.msgType === 'image' ? 'success' : 'warning')">
                            {{ row.msgType === 'text' ? '文本' : (row.msgType === 'image' ? '图片' : '文件') }}
                        </el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="消息内容/链接" min-width="300">
                    <template #default="{ row }">
                        <div v-if="row.msgType === 'text'" class="message-content"
                            v-html="highlightKeyword(row.content)">
                        </div>
                        <div v-else-if="row.msgType === 'image' || isImageFile(row.fileName)" class="message-media">
                            <el-image :src="row.fileUrl?.replace(/ /g, '%20')"
                                :preview-src-list="[row.fileUrl?.replace(/ /g, '%20')]" fit="cover"
                                class="chat-image-preview" hide-on-click-modal />
                        </div>
                        <div v-else class="message-media">
                            <el-link type="primary" :href="row.fileUrl" target="_blank">
                                <el-icon>
                                    <Document />
                                </el-icon> {{ row.fileName || '下载附件' }}
                            </el-link>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="发送时间" width="160" align="center">
                    <template #default="{ row }">
                        {{ formatTime(row.createdAt) }}
                    </template>
                </el-table-column>
            </el-table>

            <div class="pagination-container">
                <el-pagination v-model:current-page="currentPage" v-model:page-size="pageSize"
                    :page-sizes="[20, 50, 100]" :total="total" layout="total, sizes, prev, pager, next, jumper"
                    @size-change="handleSizeChange" @current-change="handleCurrentChange" />
            </div>
        </el-card>

        <!-- Chat History Drawer -->
        <el-drawer v-model="drawerVisible" title="聊天上下文" size="500px" direction="rtl" :destroy-on-close="true"
            class="chat-history-drawer">
            <div class="drawer-header-info" v-if="drawerTarget">
                当前查看: <strong>{{ drawerTarget.senderName }}</strong> (房间 ID: {{ drawerTarget.roomId }})
            </div>

            <div class="drawer-chat-container" v-loading="drawerLoading">
                <div v-if="historyMessages.length === 0 && !drawerLoading" class="empty-history">
                    暂无聊天记录
                </div>

                <div v-for="(msg, index) in historyMessages" :key="msg.id || index" class="history-message-wrapper"
                    :class="{ 'message-mine': msg.senderId === drawerTarget?.senderId }">
                    <div class="message-time-sep" v-if="showHistoryTime(index)">
                        {{ formatTime(msg.createdAt) }}
                    </div>

                    <div class="message-content-row">
                        <el-avatar class="msg-avatar" :size="36" :src="msg.avatar || defaultAvatar" />
                        <div class="msg-bubble-container">
                            <div class="msg-sender-name">{{ msg.senderName }}</div>
                            <div class="msg-bubble" :class="{
                                ['msg-type-' + msg.msgType]: true,
                                'msg-type-image': isImageFile(msg.fileName)
                            }">
                                <template v-if="msg.msgType === 'text'">
                                    <div v-html="highlightKeyword(msg.content)"></div>
                                </template>
                                <template v-else-if="msg.msgType === 'image' || isImageFile(msg.fileName)">
                                    <el-image :src="msg.fileUrl?.replace(/ /g, '%20')"
                                        :preview-src-list="[msg.fileUrl?.replace(/ /g, '%20')]" fit="cover"
                                        class="drawer-chat-img" hide-on-click-modal />
                                </template>
                                <template v-else>
                                    <el-link type="primary" :href="msg.fileUrl" target="_blank">
                                        <el-icon>
                                            <Document />
                                        </el-icon> {{ msg.fileName || '附件下载' }}
                                    </el-link>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </el-drawer>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { Search, User, Document } from '@element-plus/icons-vue'
import api from '@/utils/api'

const messages = ref([])
const loading = ref(false)
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(20)

// Search Context
const searchQuery = ref('')
const searchUserId = ref('')
const defaultAvatar = "https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png"

// Drawer State
const drawerVisible = ref(false)
const drawerLoading = ref(false)
const historyMessages = ref([])
const drawerTarget = ref(null)

const isImageFile = (fileName) => {
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName || '');
}

const fetchMessages = async () => {
    loading.value = true
    try {
        const res = await api.get('/api/admin/chat-messages', {
            params: {
                page: currentPage.value,
                pageSize: pageSize.value,
                query: searchQuery.value,
                userId: searchUserId.value
            }
        })
        messages.value = res.data.messages || []
        total.value = res.data.total || 0
    } catch (e) {
        console.error('Failed to fetch chat messages', e)
    } finally {
        loading.value = false
    }
}

const handleSearch = () => {
    currentPage.value = 1
    fetchMessages()
}

const handleSizeChange = (val) => {
    pageSize.value = val
    fetchMessages()
}

const handleCurrentChange = (val) => {
    currentPage.value = val
    fetchMessages()
}

const formatTime = (timestamp) => {
    if (!timestamp) return '-'
    const date = new Date(timestamp * 1000)
    return date.getFullYear() + '-' +
        String(date.getMonth() + 1).padStart(2, '0') + '-' +
        String(date.getDate()).padStart(2, '0') + ' ' +
        String(date.getHours()).padStart(2, '0') + ':' +
        String(date.getMinutes()).padStart(2, '0') + ':' +
        String(date.getSeconds()).padStart(2, '0')
}

// Highlight the searched keyword in text for easier monitoring
const highlightKeyword = (text) => {
    if (!text) return ''
    if (!searchQuery.value) return text

    // Simple sanitization to prevent XSS before injecting HTML
    let safeText = text.replace(/</g, "&lt;").replace(/>/g, "&gt;")
    const searchRegex = new RegExp(`(${searchQuery.value})`, 'gi')
    return safeText.replace(searchRegex, '<span style="color: red; font-weight: bold; background-color: #fee2e2; padding: 0 2px;">$1</span>')
}

// Drawer Methods
const openHistoryDrawer = async (row) => {
    drawerTarget.value = row
    drawerVisible.value = true
    drawerLoading.value = true
    historyMessages.value = []

    try {
        const res = await api.get('/api/admin/chat-history', {
            params: { roomId: row.roomId }
        })
        historyMessages.value = res.data.messages || []
    } catch (e) {
        console.error('Failed to fetch chat history', e)
    } finally {
        drawerLoading.value = false
    }
}

const showHistoryTime = (index) => {
    if (index === 0) return true
    const current = historyMessages.value[index]
    const prev = historyMessages.value[index - 1]
    if (!current || !prev) return false
    // Show time if more than 5 minutes elapsed
    return (current.createdAt - prev.createdAt) > 300
}

onMounted(() => {
    fetchMessages()
})
</script>

<style scoped>
.admin-chat-manager {
    padding: 20px;
}

.manager-card {
    border-radius: 12px;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.title {
    font-size: 18px;
    font-weight: bold;
    color: #303133;
}

.header-actions {
    display: flex;
    gap: 10px;
}

.user-cell {
    display: flex;
    align-items: center;
    gap: 12px;
}

.user-info {
    display: flex;
    flex-direction: column;
}

.username {
    font-weight: 500;
    color: #303133;
}

.user-id {
    font-size: 12px;
    color: #909399;
}

.message-content {
    white-space: pre-wrap;
    word-break: break-all;
    line-height: 1.5;
    color: #606266;
}

.chat-image-preview {
    width: 80px;
    height: 80px;
    border-radius: 4px;
    border: 1px solid #ebeef5;
}

.pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
}

/* Clickable User Styles */
.clickable-user {
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    transition: background-color 0.2s;
}

.clickable-user:hover {
    background-color: #f0f2f5;
}

/* Drawer Styles */
.drawer-header-info {
    padding: 10px 20px;
    background-color: #f5f7fa;
    border-bottom: 1px solid #e4e7ed;
    color: #606266;
    font-size: 14px;
}

.drawer-chat-container {
    padding: 20px;
    height: 100%;
    overflow-y: auto;
    background-color: #f5f5f5;
}

.empty-history {
    text-align: center;
    color: #909399;
    padding: 40px 0;
}

/* Chat bubble aesthetics inside drawer */
.history-message-wrapper {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
}

.message-time-sep {
    text-align: center;
    font-size: 12px;
    color: #b0b0b0;
    margin-bottom: 10px;
}

.message-content-row {
    display: flex;
    align-items: flex-start;
}

.msg-bubble-container {
    max-width: 75%;
    margin-left: 10px;
}

.msg-sender-name {
    font-size: 12px;
    color: #909399;
    margin-bottom: 4px;
    margin-left: 2px;
}

.msg-bubble {
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 14px;
    line-height: 1.5;
    background-color: white;
    color: #333;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    word-break: break-word;
}

.drawer-chat-img {
    max-width: 200px;
    max-height: 200px;
    border-radius: 4px;
}

/* My/Target user bubble styling */
.history-message-wrapper.message-mine .message-content-row {
    flex-direction: row-reverse;
}

.history-message-wrapper.message-mine .msg-bubble-container {
    margin-left: 0;
    margin-right: 10px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.history-message-wrapper.message-mine .msg-sender-name {
    margin-left: 0;
    margin-right: 2px;
}

.history-message-wrapper.message-mine .msg-bubble {
    background-color: #95ec69;
    /* WeChat green */
    color: #000;
}

.msg-bubble.msg-type-image,
.msg-bubble.msg-type-file {
    background-color: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding: 2px !important;
}
</style>
